import { _debug } from '$extensionSrc/utils/logging'

import { deleteRecordPath, getAuthorFeedPath } from './blueskyUrls'

const BLUESKY_STORAGE_KEY = 'BSKY_STORAGE'

// Get needed values from Bluesky window local storage to make requests
async function getBlueskyRequestTokens(): Promise<{
  accessJwt: string
  refreshJwt: string
  agentLabeler: string
}> {
  const bskyStorageObj = JSON.parse(
    (await window.localStorage.getItem(BLUESKY_STORAGE_KEY)) || '{}',
  )

  const accessJwt = bskyStorageObj?.session?.currentAccount?.accessJwt
  const refreshJwt = bskyStorageObj?.session?.currentAccount?.refreshJwt

  const agentLabelerStorageKey = `agent-labelers:${bskyStorageObj?.session?.currentAccount?.did}`

  const agentLabelerFromStorage = JSON.parse(
    (await window.localStorage.getItem(agentLabelerStorageKey)) || '[]',
  ) as string[]
  const agentLabelersArray = Array.from(agentLabelerFromStorage)

  return { accessJwt, refreshJwt, agentLabeler: agentLabelersArray[0] }
}

async function getBlueskyPdsHost(): Promise<string> {
  const bskyStorageObj = JSON.parse(
    (await window.localStorage.getItem(BLUESKY_STORAGE_KEY)) || '{}',
  )

  return bskyStorageObj?.session?.currentAccount?.pdsUrl
}

async function blueskyFetch(
  input: RequestInfo | URL,
  options: RequestInit = {},
): Promise<{
  response: Response
  rateLimitReset: string
  rateLimitRemaining: number
}> {
  try {
    const { accessJwt, refreshJwt, agentLabeler } =
      await getBlueskyRequestTokens()

    const response = await fetch(input, {
      ...options,
      headers: {
        'Atproto-Accept-Labelers': `${agentLabeler};redact`,
        Authorization: `Bearer ${accessJwt || refreshJwt}`,
        'Content-Type': 'application/json',
      },
    })

    const rateLimitReset = response.headers.get('ratelimit-reset')
    const rateLimitRemaining = parseInt(
      response.headers.get('ratelimit-remaining') || '0',
      10,
    )

    return { response, rateLimitReset, rateLimitRemaining }
  } catch (error) {
    _debug(
      `Bluesky Request error: ${error.message}`,
      'blueskyRequestUtils.blueskyFetch',
      'e',
    )
    throw error
  }
}

type Post = {
  post: {
    uri: string
    author: {
      did: string // Can use this to determine if it's a repost
    }
    record: {
      $type: string
      createdAt: string
      text: string
    }
    viewer: {
      repost: string
    }
  }
  reason?: {
    by: {
      did: string
      createdAt: string // Repost timestamp
    }
  }
}

type GetAuthorFeedResponse = {
  cursor?: string
  feed: Post[]
}

export async function getAuthorFeed(
  userId: string,
  cursor?: string,
): Promise<GetAuthorFeedResponse> {
  const personalDataServerHost = await getBlueskyPdsHost()
  const getAuthorUrl = new URL(getAuthorFeedPath, personalDataServerHost)
  getAuthorUrl.searchParams.set('actor', userId)
  getAuthorUrl.searchParams.set('filter', 'posts_and_author_threads')
  getAuthorUrl.searchParams.set('limit', '30')
  getAuthorUrl.searchParams.set('includePins', 'true')

  if (cursor) {
    getAuthorUrl.searchParams.set('cursor', cursor)
  }

  const { response } = await blueskyFetch(getAuthorUrl, {
    method: 'GET',
  })

  const data: GetAuthorFeedResponse = await response.json()

  _debug(
    `BLUESKY retrieved ${data.feed.length} posts. Cursor used: ${cursor}`,
    'blueskyRequestUtils.getAuthorFeed',
    'd',
  )

  return data
}

type DeleteRecordBody = {
  collection: string
  repo: string
  rkey: string
}

export async function deletePost(
  postId: string,
  userId: string,
  isRepost: boolean,
): Promise<{
  response: Response
  rateLimitReset: string
  rateLimitRemaining: number
}> {
  const personalDataServerHost = await getBlueskyPdsHost()
  const collection = isRepost ? 'app.bsky.feed.repost' : 'app.bsky.feed.post'

  const body: DeleteRecordBody = {
    collection,
    repo: userId,
    rkey: postId,
  }
  const deleteRecordUrl = new URL(deleteRecordPath, personalDataServerHost)

  const { response, rateLimitReset, rateLimitRemaining } = await blueskyFetch(
    deleteRecordUrl,
    {
      method: 'POST',
      body: JSON.stringify(body),
    },
  )

  return { response, rateLimitReset, rateLimitRemaining }
}
